import {
  Box, Flex, Text, Button, Icon,
} from '@chakra-ui/react';
import { LuArrowLeft, LuArrowRight, LuSparkles, LuLock } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import useIsPhone from '../../hooks/useIsPhone';
import OwnerAvatar from '../shared/OwnerAvatar';
import RoleChip from './RoleChip';
import { PrimaryButton } from './OnboardingShell';

// The card's content column, as the owner flow's closing step draws it.
const COL = '440px';

/**
 * The guest flow's last stop: where the button leads, drawn with the
 * switcher's own wording so the space reads the same once they are in it, and
 * a pointer to the private space they also have.
 *
 * Its button is the only way out, and it is the parent's: it stamps the flow
 * finished and moves into the host's space.
 */
export default function GuestFinish({
  workspace, role, roleLabel, name, entering, onEnter, onBack,
}) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const muted = isPhone ? 'phone.muted' : 'redesign.text.muted';

  // The switcher's "Personal de Laura" and "compartido por Laura Gómez", from
  // the names only: never the email. `owner_first_name` is the backend's
  // cleaned first name, null when `owner_full_name` is an address (some
  // accounts store one there), so nothing is rebuilt from the full name: it is
  // shown only beside a first name, and only when it holds no address either.
  // Without a first name the card is the workspace's name alone.
  const ownerFirst = workspace?.owner_first_name || null;
  const fullName = workspace?.owner_full_name || '';
  const ownerFull = ownerFirst
    ? (fullName && !fullName.includes('@') ? fullName : ownerFirst)
    : null;
  const cardTitle = ownerFirst
    ? t('workspaces:switcher.sharedTitle', { owner: ownerFirst, name: workspace?.name })
    : workspace?.name || name;

  return (
    <Box>
      <Flex
        boxSize="56px"
        borderRadius="16px"
        align="center"
        justify="center"
        bg="rgba(21, 128, 61, 0.10)"
        _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
        mb="18px"
      >
        <Icon as={LuSparkles} boxSize="26px" strokeWidth={1.6} color="redesign.amount.income" />
      </Flex>
      <Text fontSize="21px" fontWeight={700} letterSpacing="-0.02em" color="redesign.heading" mb="8px">
        {t('guest.done.title')}
      </Text>
      {/* The phone gets no tour (the owner's doesn't run there either), so it
          is not promised one. */}
      <Text fontSize="14px" lineHeight="1.6" color={muted} mb="20px" maxW={COL}>
        {t(isPhone ? 'guest.done.bodyPhone' : 'guest.done.body', { name, role: roleLabel })}
      </Text>

      <Flex
        align="center"
        gap="12px"
        p="14px"
        mb="20px"
        maxW={isPhone ? 'none' : COL}
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="14px"
        bg="redesign.bg.surface"
      >
        <OwnerAvatar workspace={workspace} size={40} />
        <Box flex="1" minW={0}>
          <Text fontSize={isPhone ? '16px' : '14px'} fontWeight={600} color="redesign.heading" noOfLines={1}>
            {cardTitle}
          </Text>
          {ownerFull && (
            <Text fontSize="13px" color={muted} noOfLines={1}>
              {t('workspaces:switcher.sharedByOther', { owner: ownerFull })}
            </Text>
          )}
        </Box>
        <RoleChip role={role} />
      </Flex>

      {isPhone ? (
        <Button
          variant="phonePrimary"
          w="100%"
          h="52px"
          fontSize="17px"
          borderRadius="14px"
          rightIcon={<Icon as={LuArrowRight} boxSize="18px" />}
          onClick={onEnter}
          isLoading={entering}
        >
          {t('guest.done.enter', { name })}
        </Button>
      ) : (
        <PrimaryButton onClick={onEnter} isLoading={entering}>
          {t('guest.done.enter', { name })}
        </PrimaryButton>
      )}

      <Flex align="flex-start" gap="10px" mt="20px" maxW={isPhone ? 'none' : COL}>
        <Icon as={LuLock} boxSize="16px" strokeWidth={1.8} color={muted} mt="2px" flexShrink={0} />
        <Text fontSize="13px" lineHeight="1.5" color={muted}>
          {t(isPhone ? 'guest.done.ownSpacePhone' : 'guest.done.ownSpace')}
        </Text>
      </Flex>

      {/* The button above is the way forward; this one only goes back. */}
      <Flex
        align="center"
        justify="flex-start"
        mt="24px"
        pt="20px"
        borderTopWidth="1px"
        borderTopColor="redesign.border"
      >
        <Button
          h="auto" px="12px" py="8px" fontSize="13px" fontWeight={600} borderRadius="10px"
          variant="ghost" color="redesign.text.muted"
          _hover={{ bg: 'redesign.bg.hover', color: 'redesign.text' }}
          leftIcon={<Icon as={LuArrowLeft} boxSize="16px" />}
          onClick={onBack}
          isDisabled={entering}
        >
          {t('wizard.back')}
        </Button>
      </Flex>
    </Box>
  );
}
